/* global APP */

import UIEvents from '../../../../service/UI/UIEvents';
import { openPermissionGuidanceDialog, processExternalDeviceRequest } from '../../device-selection';
import { showNotification, showWarningNotification } from '../../notifications';
import {
    replaceAudioTrackById,
    replaceVideoTrackById,
    setDeviceStatusWarning
} from '../../prejoin/actions';
import { isPrejoinPageVisible } from '../../prejoin/functions';
import { APP_WILL_MOUNT, APP_WILL_UNMOUNT } from '../app';
import MeetHourJS, {
    MHMediaDevicesEvents,
    MHTrackErrors
} from '../lib-meet-hour';
import { MiddlewareRegistry } from '../redux';
import { updateSettings } from '../settings';

import {
    CHECK_AND_NOTIFY_FOR_NEW_DEVICE,
    NOTIFY_CAMERA_ERROR,
    NOTIFY_MIC_ERROR,
    SET_AUDIO_INPUT_DEVICE,
    SET_VIDEO_INPUT_DEVICE,
    UPDATE_DEVICE_LIST
} from './actionTypes';
import {
    devicePermissionsChanged,
    removePendingDeviceRequests,
    setAudioInputDevice,
    setVideoInputDevice
} from './actions';
import {
    areDeviceLabelsInitialized,
    formatDeviceLabel,
    groupDevicesByKind,
    setAudioOutputDeviceId
} from './functions';
import logger from './logger';

const MH_TRACK_ERROR_TO_MESSAGE_KEY_MAP = {
    microphone: {
        [MHTrackErrors.CONSTRAINT_FAILED]: 'dialog.micConstraintFailedError',
        [MHTrackErrors.GENERAL]: 'dialog.micUnknownError',
        [MHTrackErrors.NOT_FOUND]: 'dialog.micNotFoundError',
        [MHTrackErrors.PERMISSION_DENIED]: 'dialog.micPermissionDeniedError',
        [MHTrackErrors.TIMEOUT]: 'dialog.micTimeoutError'
    },
    camera: {
        [MHTrackErrors.CONSTRAINT_FAILED]: 'dialog.cameraConstraintFailedError',
        [MHTrackErrors.GENERAL]: 'dialog.cameraUnknownError',
        [MHTrackErrors.NOT_FOUND]: 'dialog.cameraNotFoundError',
        [MHTrackErrors.PERMISSION_DENIED]: 'dialog.cameraPermissionDeniedError',
        [MHTrackErrors.UNSUPPORTED_RESOLUTION]:
      'dialog.cameraUnsupportedResolutionError',
        [MHTrackErrors.TIMEOUT]: 'dialog.cameraTimeoutError'
    }
};

/**
 * A listener for device permissions changed reported from lib-meet-hour.
 */
let permissionsListener;

/**
 * Logs the current device list.
 *
 * @param {Object} deviceList - Whatever is returned by {@link groupDevicesByKind}.
 * @returns {string}
 */
function logDeviceList(deviceList) {
    const devicesToStr = list =>
        list.map(device => `\t\t${device.label}[${device.deviceId}]`).join('\n');
    const audioInputs = devicesToStr(deviceList.audioInput);
    const audioOutputs = devicesToStr(deviceList.audioOutput);
    const videoInputs = devicesToStr(deviceList.videoInput);

    logger.debug(
    'Device list updated:\n'
      + `audioInput:\n${audioInputs}\n`
      + `audioOutput:\n${audioOutputs}\n`
      + `videoInput:\n${videoInputs}`
    );
}

/**
 * Sets the default devices in the settings if they are not already set.
 * @param {Store} store - The redux store.
 * @param {Array<MediaDeviceInfo>} devices - The array of all available devices.
 * @returns {void}
 */
function _setDefaultDevices({ dispatch, getState }, devices) {
    const state = getState();
    const {
        userSelectedCameraDeviceId,
        userSelectedMicDeviceId,
        userSelectedAudioOutputDeviceId,
        audioOutputDeviceId
    } = state['features/base/settings'];

    // Until permission is granted, enumerateDevices() returns one placeholder per kind with an empty deviceId. It names
    // no real device, so it must neither overwrite the user's saved choice nor be handed to getUserMedia - which is what
    // re-ran a denied getUserMedia (and re-opened the permission dialog) on every devicechange.
    const { audioInput, audioOutput, videoInput } = groupDevicesByKind(devices.filter(d => d.deviceId));

    if (audioInput.length > 0) {
        const isSelectedMicValid = userSelectedMicDeviceId
            && audioInput.some(d => d.deviceId === userSelectedMicDeviceId);

        if (!isSelectedMicValid) {
            const defaultMic = audioInput.find(d => d.deviceId === 'default') || audioInput[0];

            dispatch(updateSettings({
                userSelectedMicDeviceId: defaultMic.deviceId,
                userSelectedMicDeviceLabel: defaultMic.label
            }));

            // Persist the default selection into the live device store too. The previous behaviour only updated the
            // settings slice, so the device-selection popup highlighted nothing on first open and the conference never
            // had a microphone to fall back on. The user reported it as "no microphone selected in the dialog" - this
            // is the seed that closes that gap. We only fire this when the conference is already in the call; on the
            // prejoin screen the popup/middleware handles its own dispatch and we don't want to double-run gUM.
            if (typeof APP !== 'undefined' && APP.conference && APP.conference._localTracksInitialized
                    && !isPrejoinPageVisible(getState())) {
                dispatch(setAudioInputDevice(defaultMic.deviceId));
            }
        }
    }

    if (videoInput.length > 0) {
        const isSelectedCameraValid = userSelectedCameraDeviceId
            && videoInput.some(d => d.deviceId === userSelectedCameraDeviceId);

        if (!isSelectedCameraValid) {
            const defaultCamera = videoInput.find(d => d.deviceId === 'default') || videoInput[0];

            dispatch(updateSettings({
                userSelectedCameraDeviceId: defaultCamera.deviceId,
                userSelectedCameraDeviceLabel: defaultCamera.label
            }));

            if (typeof APP !== 'undefined' && APP.conference && APP.conference._localTracksInitialized
                    && !isPrejoinPageVisible(getState())) {
                dispatch(setVideoInputDevice(defaultCamera.deviceId));
            }
        }
    }

    if (audioOutput.length > 0) {
        const isSelectedOutputValid = userSelectedAudioOutputDeviceId
            && audioOutput.some(d => d.deviceId === userSelectedAudioOutputDeviceId);

        const targetOutputDevice = isSelectedOutputValid
            ? audioOutput.find(d => d.deviceId === userSelectedAudioOutputDeviceId)
            : audioOutput.find(d => d.deviceId === 'default') || audioOutput[0];

        if (!isSelectedOutputValid) {
            dispatch(updateSettings({
                userSelectedAudioOutputDeviceId: targetOutputDevice.deviceId,
                userSelectedAudioOutputDeviceLabel: targetOutputDevice.label
            }));
        }

        if (audioOutputDeviceId !== targetOutputDevice.deviceId) {
            setAudioOutputDeviceId(targetOutputDevice.deviceId, dispatch, !isSelectedOutputValid, targetOutputDevice.label)
                .then(() => logger.log(`changed audio output device to ${targetOutputDevice.deviceId}`))
                .catch(err => {
                    logger.warn(
                        `Failed to change audio output device to ${targetOutputDevice.deviceId}`,
                        err
                    );
                });
        }
    }
}

/**
 * Implements the middleware of the feature base/devices.
 *
 * @param {Store} store - Redux store.
 * @returns {Function}
 */

MiddlewareRegistry.register(store => next => action => {
    switch (action.type) {
    case APP_WILL_MOUNT: {
        const _permissionsListener = permissions => {
            store.dispatch(devicePermissionsChanged(permissions));
        };
        const { mediaDevices } = MeetHourJS;

        permissionsListener = _permissionsListener;
        mediaDevices.addEventListener(
        MHMediaDevicesEvents.PERMISSIONS_CHANGED,
        permissionsListener
        );
        Promise.all([
            mediaDevices.isDevicePermissionGranted('audio'),
            mediaDevices.isDevicePermissionGranted('video')
        ])
        .then(results => {
            _permissionsListener({
                audio: results[0],
                video: results[1]
            });
        })
        .catch(() => {
            // Ignore errors.
            _setDefaultDevices(store, action.devices);
        });
        break;
    }
    case APP_WILL_UNMOUNT:
        if (typeof permissionsListener === 'function') {
            MeetHourJS.mediaDevices.removeEventListener(
          MHMediaDevicesEvents.PERMISSIONS_CHANGED,
          permissionsListener
            );
            permissionsListener = undefined;
        }
        break;
    case NOTIFY_CAMERA_ERROR: {
        if (typeof APP !== 'object' || !action.error) {
            break;
        }

        const { message, name } = action.error;

        // If this is a permission denied error, show the guidance dialog
        if (name === MHTrackErrors.PERMISSION_DENIED) {
            store.dispatch(openPermissionGuidanceDialog([ 'video' ]));
        }

        const cameraMHTrackErrorMsg
            = MH_TRACK_ERROR_TO_MESSAGE_KEY_MAP.camera[name];
        const cameraErrorMsg
            = cameraMHTrackErrorMsg
            || MH_TRACK_ERROR_TO_MESSAGE_KEY_MAP.camera[MHTrackErrors.GENERAL];
        const additionalCameraErrorMsg = cameraMHTrackErrorMsg ? null : message;
        const titleKey = 'deviceError.cameraError';

        store.dispatch(
            showWarningNotification({
                description: additionalCameraErrorMsg,
                descriptionKey: cameraErrorMsg,
                titleKey
            })
        );

        if (isPrejoinPageVisible(store.getState())) {
            store.dispatch(setDeviceStatusWarning(titleKey));
        }

        break;
    }
    case NOTIFY_MIC_ERROR: {
        if (typeof APP !== 'object' || !action.error) {
            break;
        }

        const { message, name } = action.error;

        // If this is a permission denied error, show the guidance dialog
        if (name === MHTrackErrors.PERMISSION_DENIED) {
            store.dispatch(openPermissionGuidanceDialog([ 'audio' ]));
        }

        const micMHTrackErrorMsg
            = MH_TRACK_ERROR_TO_MESSAGE_KEY_MAP.microphone[name];
        const micErrorMsg
            = micMHTrackErrorMsg
            || MH_TRACK_ERROR_TO_MESSAGE_KEY_MAP.microphone[MHTrackErrors.GENERAL];
        const additionalMicErrorMsg = micMHTrackErrorMsg ? null : message;
        const titleKey = 'deviceError.microphoneError';

        store.dispatch(
            showWarningNotification({
                description: additionalMicErrorMsg,
                descriptionKey: micErrorMsg,
                titleKey
            })
        );

        if (isPrejoinPageVisible(store.getState())) {
            store.dispatch(setDeviceStatusWarning(titleKey));
        }

        break;
    }
    case SET_AUDIO_INPUT_DEVICE:
        if (isPrejoinPageVisible(store.getState())) {
            store.dispatch(replaceAudioTrackById(action.deviceId));
        } else {
            APP.conference.onAudioDeviceChanged(action.deviceId);

            // APP.UI.emitEvent(UIEvents.AUDIO_DEVICE_CHANGED, action.deviceId);
        }
        break;
    case SET_VIDEO_INPUT_DEVICE:
        if (isPrejoinPageVisible(store.getState())) {
            store.dispatch(replaceVideoTrackById(action.deviceId));
        } else {
            APP.UI.emitEvent(UIEvents.VIDEO_DEVICE_CHANGED, action.deviceId);
        }
        break;
    case UPDATE_DEVICE_LIST:
        logDeviceList(groupDevicesByKind(action.devices));
        _setDefaultDevices(store, action.devices);
        if (areDeviceLabelsInitialized(store.getState())) {
            return _processPendingRequests(store, next, action);
        }
        break;
    case CHECK_AND_NOTIFY_FOR_NEW_DEVICE:
        _checkAndNotifyForNewDevice(store, action.newDevices, action.oldDevices);
        break;
    }

    return next(action);
});

/**
 * Does extra sync up on properties that may need to be updated after the
 * conference was joined.
 *
 * @param {Store} store - The redux store in which the specified {@code action}
 * is being dispatched.
 * @param {Dispatch} next - The redux {@code dispatch} function to dispatch the
 * specified {@code action} to the specified {@code store}.
 * @param {Action} action - The redux action {@code CONFERENCE_JOINED} which is
 * being dispatched in the specified {@code store}.
 * @private
 * @returns {Object} The value returned by {@code next(action)}.
 */
function _processPendingRequests({ dispatch, getState }, next, action) {
    const result = next(action);
    const state = getState();
    const { pendingRequests } = state['features/base/devices'];

    if (!pendingRequests || pendingRequests.length === 0) {
        return result;
    }

    pendingRequests.forEach(request => {
        processExternalDeviceRequest(
      dispatch,
      getState,
      request,
      request.responseCallback
        );
    });
    dispatch(removePendingDeviceRequests());

    return result;
}

/**
 * Finds a new device by comparing new and old array of devices and dispatches
 * notification with the new device. Shows only one notification for all new devices.
 *
 * @param {Store} store - The redux store in which the specified {@code action}
 * is being dispatched.
 * @param {MediaDeviceInfo[]} newDevices - The array of new devices we received.
 * @param {MediaDeviceInfo[]} oldDevices - The array of the old devices we have.
 * @private
 * @returns {void}
 */
function _checkAndNotifyForNewDevice(store, newDevices, oldDevices) {
    const { dispatch } = store;

    // let's intersect both newDevices and oldDevices and handle the newly
    // added devices
    const onlyNewDevices = newDevices.filter(
    nDevice =>
        !oldDevices.find(device => device.deviceId === nDevice.deviceId)
    );

    if (onlyNewDevices.length === 0) {
        return;
    }

    // Group devices by groupID, but handle cases where groupId might be undefined/empty
    const devicesGroupBy = onlyNewDevices.reduce((accumulated, value) => {
        const groupKey = value.groupId || `ungrouped_${value.deviceId}`;

        accumulated[groupKey] = accumulated[groupKey] || [];
        accumulated[groupKey].push(value);

        return accumulated;
    }, {});

    const deviceGroups = Object.values(devicesGroupBy).filter(
    group => group.length > 0
    );

    if (deviceGroups.length === 0) {
        return;
    }

    // Show only ONE notification for all new devices
    // Use the first device group as reference for the notification
    const firstDeviceGroup = deviceGroups[0];
    const referenceDevice = firstDeviceGroup[0];

    // Collect all devices from all groups for the action handler
    const allNewDevices = deviceGroups.flat();

    // Determine notification type based on the types of devices present
    const hasVideo = allNewDevices.some(device => device.kind === 'videoinput');
    const hasAudio = allNewDevices.some(
    device => device.kind === 'audioinput' || device.kind === 'audiooutput'
    );

    let titleKey;
    let description;

    if (hasVideo && hasAudio) {
        titleKey = 'notify.newDeviceTitle'; // Generic title for mixed devices
        description = 'New audio and video devices detected';
    } else if (hasVideo) {
        titleKey = 'notify.newDeviceCameraTitle';
        description = formatDeviceLabel(referenceDevice.label);
    } else if (hasAudio) {
        titleKey = 'notify.newDeviceAudioTitle';
        description = formatDeviceLabel(referenceDevice.label);
    } else {
        titleKey = 'notify.newDeviceTitle';
        description = 'New device detected';
    }

    // Show single notification with all devices
    dispatch(
    showNotification({
        description,
        titleKey,
        customActionNameKey: 'notify.newDeviceAction',
        customActionHandler: _useDevice.bind(undefined, store, allNewDevices)
    })
    );
}

/**
 * Set a device to be currently used, selected by the user.
 *
 * @param {Store} store - The redux store in which the specified {@code action}
 * is being dispatched.
 * @param {Array<MediaDeviceInfo|InputDeviceInfo>} devices - The devices to save.
 * @returns {boolean} - Returns true in order notifications to be dismissed.
 * @private
 */
function _useDevice({ dispatch }, devices) {
    devices.forEach(device => {
        switch (device.kind) {
        case 'videoinput': {
            dispatch(
          updateSettings({
              userSelectedCameraDeviceId: device.deviceId,
              userSelectedCameraDeviceLabel: device.label
          })
            );

            dispatch(setVideoInputDevice(device.deviceId));
            break;
        }
        case 'audioinput': {
            dispatch(
          updateSettings({
              userSelectedMicDeviceId: device.deviceId,
              userSelectedMicDeviceLabel: device.label
          })
            );

            dispatch(setAudioInputDevice(device.deviceId));
            break;
        }
        case 'audiooutput': {
            setAudioOutputDeviceId(device.deviceId, dispatch, true, device.label)
          .then(() => logger.log('changed audio output device'))
          .catch(err => {
              logger.warn(
              'Failed to change audio output device.',
              'Default or previously set audio output device will',
              ' be used instead.',
              err
              );
          });
            break;
        }
        }
    });

    return true;
}
